import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import { MapPin, X, Search, Crosshair } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
	iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function ClickHandler({ onMapClick }) {
	useMapEvents({ click(e) { onMapClick(e.latlng); } });
	return null;
}

function PinRecenter({ pin, radius }) {
	const map = useMap();
	const did = useRef(false);
	useEffect(() => {
		if (pin && !did.current) {
			did.current = true;
			const z = Math.max(7, Math.min(11, Math.round(13 - Math.log2(radius || 50))));
			setTimeout(() => map.setView([pin.lat, pin.lng], z), 50);
		}
	}, [pin, radius, map]);
	return null;
}

export default function MapFilter({ onSearch, pin, setPin, open, setOpen, radius, setRadius, geoActive, setGeoActive }) {
	const { t } = useTranslation();
	const [geoLoading, setGeoLoading] = useState(false);

	const handleMapClick = useCallback((latlng) => setPin(latlng), []);

	const activateGeoSearch = () => {
		if (!navigator.geolocation) return;
		setGeoLoading(true);
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const lat = pos.coords.latitude;
				const lng = pos.coords.longitude;
				setPin({ lat, lng });
				setGeoActive(true);
				setGeoLoading(false);
			},
			() => { setGeoLoading(false); },
			{ enableHighAccuracy: true, timeout: 10000 }
		);
	};

	const handleSearch = () => {
		if (!pin) return;
		onSearch();
	};

	if (!open) {
		return (
			<button
				onClick={() => setOpen(true)}
				className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E6E2D6] rounded-sm text-sm text-[#1A3C34]
				 hover:border-[#8B4513] transition-colors"
				data-testid="map-filter-toggle">
				<MapPin className="w-4 h-4 text-[#8B4513]" />
				{t('mapSearch.title')}
			</button>
		);
	}

	return (
		<div className="border border-[#E6E2D6] rounded-sm bg-white md:p-4 md:mb-4" data-testid="map-filter-panel">
			<div className="flex items-center justify-between mb-3">
				<h3 className="text-sm font-semibold text-[#1A3C34]">{t('mapSearch.title')}</h3>
				<button onClick={() => { setOpen(false); }}
								className="text-[#666055] hover:text-[#1A3C34]">
					<X className="w-4 h-4" />
				</button>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<div className="md:col-span-2 h-52 rounded-sm overflow-hidden border border-[#E6E2D6]">
					<MapContainer center={[39.5, -8.0]} zoom={6} className="h-full w-full" style={{ zIndex: 1 }}>
						<TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OSM' />
						<ClickHandler onMapClick={handleMapClick} />
						<PinRecenter pin={pin} radius={radius} />
						{ pin && <Marker position={pin} />}
						{ pin && <Circle center={pin} radius={radius * 1000}
														 pathOptions={{ color: '#8B4513', fillColor: '#8B4513', fillOpacity: 0.08, weight: 2 }} />
						}
					</MapContainer>
				</div>
				<div className="flex flex-col justify-center gap-3">
					<div>
						<p className="text-xs font-semibold text-[#666055] uppercase tracking-wider mb-1">
							{t('mapSearch.radius')}
						</p>
						<Slider value={[radius]} onValueChange={(v) => setRadius(v[0])} min={5} max={200} step={5} />
						<p className="text-sm text-[#8B4513] font-bold mt-1">{radius} km</p>
					</div>
					<Button onClick={ activateGeoSearch } disabled={ geoLoading } variant="outline"
									className={`w-full h-10 rounded-sm text-sm flex items-center gap-2 ${geoActive ? 'bg-[#1A3C34] text-white ' +
										'border-[#1A3C34] hover:bg-[#153029]' : 'border-[#E6E2D6] text-[#1A3C34] hover:bg-[#F2F0E9]'}`}>
						<Crosshair className="w-4 h-4" />
						{ geoLoading ?
							t('common.loading') : geoActive ? t('search.nearby_active') : t('search.use_my_location')
						}
					</Button>
					<Button onClick={ handleSearch } disabled={ !pin } className="bg-[#8B4513] hover:bg-[#6F3610] text-white rounded-sm h-9
								text-sm btn-equi" data-testid="map-filter-search">
						<Search className="w-4 h-4 mr-2"/>
						{ t('mapSearch.search') }
					</Button>
					{	!pin &&
						<p className="text-[10px] text-[#666055] text-center">
							{t('mapSearch.click_to_pin')}
						</p>
					}
				</div>
			</div>
		</div>
	);
}